import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, DollarSign, Loader2 } from 'lucide-react';
import type { ApiResponse, DriverPriceSettings } from '@evi/shared';
import { api, getDisplayErrorMessage } from '@/lib/api';
import styles from './SettingsPage.module.css';

/**
 * Warren's driver price settings (ruling D3, 21 October 2026). Super Admin only (Api: routes/settings/driver-price.ts).
 * The minimum margin on the driver price is the one percentage of 21 October 2026, late evening: "Floor = the site's
 * electricity cost per kWh divided by (1 minus the margin)". A New job takes the local market price, or the floor when the
 * market price is below it or the area has none yet; the ops review warns under the same percentage. The Api shows 33%
 * until someone saves another value. The markup is only the ops review's suggestion (utility cost x markup); empty, none.
 */
const KEY = ['driver-price-settings'];
const toText = (v: number | null | undefined) => (typeof v === 'number' ? String(v) : '');
const toValue = (v: string) => (v.trim() === '' ? null : Number(v));

export function DriverPriceSection() {
  const client = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => api.get('settings/driver-price').json<ApiResponse<{ value: DriverPriceSettings }>>().then(r => r.data!.value), staleTime: 60_000 });
  const [markup, setMarkup] = useState<string | null>(null), [floor, setFloor] = useState<string | null>(null);
  const [saving, setSaving] = useState(false), [error, setError] = useState(''), [saved, setSaved] = useState(false);
  const markupText = markup ?? toText(q.data?.markup), floorText = floor ?? toText(q.data?.margin_floor_pct);
  const dirty = toValue(markupText) !== (q.data?.markup ?? null) || toValue(floorText) !== (q.data?.margin_floor_pct ?? null);
  async function save() {
    setSaving(true); setError(''); setSaved(false);
    try {
      await api.put('settings/driver-price', { json: { markup: toValue(markupText), margin_floor_pct: toValue(floorText) } }).json();
      await client.invalidateQueries({ queryKey: KEY });
      setMarkup(null); setFloor(null); setSaved(true);
    } catch (err) { setError(await getDisplayErrorMessage(err, 'The settings were not saved.')); } finally { setSaving(false); }
  }
  return <section className={styles.platformFeeSection}>
    <div className={styles.platformFeeHeader}>
      <div className={styles.headerIcon}><DollarSign size={16} /></div>
      <div>
        <h2 className={styles.platformFeeTitle}>Driver price on New jobs</h2>
        <p className={styles.platformFeeHint}>Each charger type (Level 2, DC) takes the local market price per kWh from the market price table, or the floor when the
          market price is below it or the area has none yet. Floor = the site&rsquo;s electricity cost per kWh divided by (1 minus the minimum margin), rounded up to
          the next cent, in the site&rsquo;s own currency. The $1 session fee and any Host Share sit on top. Every change is audited.</p>
      </div>
    </div>
    {q.isLoading ? <p className={styles.platformFeeHint}><Loader2 size={13} className={styles.spin} /> Loading.</p>
      : q.isError ? <p className={styles.platformFeeHint} style={{ color: 'var(--color-err)' }}>Couldn&rsquo;t load the driver price settings. Refresh to retry.</p>
        : <>
          <div className={styles.platformFeeField}>
            <label className={styles.platformFeeLabel} htmlFor="driver-price-floor">Minimum margin on the driver price (%)</label>
            <div className={styles.platformFeeInputRow}>
              <input id="driver-price-floor" className={styles.platformFeeInput} type="number" min={0} max={95} step={0.1} placeholder="33"
                value={floorText} onChange={e => { setFloor(e.target.value); setSaved(false); }} disabled={saving} />
            </div>
            <p className={styles.platformFeeHint}>At 33%, 30.6 cents of electricity floors at 46 cents. Empty means 33%. The review also warns under it.</p>
          </div>
          <div className={styles.platformFeeField}>
            <label className={styles.platformFeeLabel} htmlFor="driver-price-markup">Markup for the review&rsquo;s suggestion (suggested = utility cost x markup)</label>
            <div className={styles.platformFeeInputRow}>
              <input id="driver-price-markup" className={styles.platformFeeInput} type="number" min={1} max={10} step={0.01} placeholder="e.g. 2.5"
                value={markupText} onChange={e => { setMarkup(e.target.value); setSaved(false); }} disabled={saving} />
              <button type="button" className={styles.platformFeeBtn} disabled={!dirty || saving} onClick={() => void save()}>
                {saving ? <><Loader2 size={13} className={styles.spin} /> Saving</> : saved && !dirty ? <><Check size={13} /> Saved</> : <>Save</>}
              </button>
            </div>
          </div>
          {error && <p className={styles.platformFeeHint} role="alert" style={{ color: 'var(--color-err)' }}>{error}</p>}
        </>}
  </section>;
}
